import { AuthApiError, SOCIAL_LOGIN_PENDING_KEY, useAuthClient, useOtpLogin, useSocialProviders } from '@pulse/auth-ui';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Sign in (ACS-004), built from the prototype's `viewLogin()`: with Google, or with a one-time code
 * Pulse emails. There's no password, and no two-step step: Cognito refuses an emailed code to anyone
 * who has turned MFA on, and Pulse offers no way to turn it on (ACS-004 AC-3).
 */
export type SignInStep = 'start' | 'code';

export interface SignInViewProps {
  readonly step: SignInStep;
  readonly email: string;
  readonly error: string | null;
  readonly info: string | null;
  readonly busy: boolean;
  /** Whether the API offers Google; the button shows only then. */
  readonly google: boolean;
  readonly onGoogle: () => void;
  readonly onSend: (email: string) => void;
  readonly onVerify: (code: string) => void;
  readonly onResend: () => void;
  readonly onRestart: () => void;
}

const GoogleG = (): React.ReactElement => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17z" />
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

function Title({ text, children }: { readonly text: string; readonly children: React.ReactNode }): React.ReactElement {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <h1 id="si-title" className="display" style={{ fontSize: 36, margin: 0 }}>
        {text}
      </h1>
      <span style={{ color: 'var(--ink-2)' }}>{children}</span>
    </div>
  );
}

function Note({ error, info }: { readonly error: string | null; readonly info: string | null }): React.ReactElement | null {
  if (error !== null) return <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)' }}>{error}</span>;
  if (info !== null) return <span role="status" style={{ fontSize: 13, color: 'var(--ink-2)' }}>{info}</span>;
  return null;
}

export function SignInView(props: SignInViewProps): React.ReactElement {
  const [email, setEmail] = React.useState(props.email);
  const [code, setCode] = React.useState('');
  const brand = (
    <div className="row" style={{ gap: 12 }}>
      <span className="brand-mark">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 12h4.5l2.5-6 4 12 3-9 1.5 3H22" />
        </svg>
      </span>
      <span className="stack">
        <span style={{ fontWeight: 600 }}>Ten24 Pulse</span>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Team reporting</span>
      </span>
    </div>
  );
  const body =
    props.step === 'start' ? (
      <form
        className="stack"
        style={{ gap: 18 }}
        // Pulse's own message ("Enter your work email.") rather than the browser's.
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          props.onSend(email.trim());
        }}
      >
        <Title text="Sign in">For the Ten24 team.</Title>
        {props.google ? (
          <>
            <button type="button" className="btn" disabled={props.busy} onClick={props.onGoogle}>
              <GoogleG />
              <span>Continue with Google</span>
            </button>
            <div className="signin-or">or</div>
          </>
        ) : null}
        <label className="stack" style={{ gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Work email</span>
          <input id="si-email" type="email" autoComplete="email" placeholder="name@ten24.co" value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        <Note error={props.error} info={props.info} />
        <button type="submit" className="btn primary" disabled={props.busy}>
          Email me a code
        </button>
        <span style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>No password. Pulse emails you a one-time code.</span>
      </form>
    ) : (
      <form
        className="stack"
        style={{ gap: 18 }}
        onSubmit={(event) => {
          event.preventDefault();
          props.onVerify(code.trim());
        }}
      >
        <Title text="Check your email">
          We sent a 6-digit code to <strong>{props.email}</strong>. It works for a few minutes.
        </Title>
        <label className="stack" style={{ gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Code</span>
          <input id="si-code" className="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-describedby="si-title" value={code} onChange={(event) => setCode(event.target.value)} />
        </label>
        <Note error={props.error} info={props.info} />
        <button type="submit" className="btn primary" disabled={props.busy}>
          Sign in
        </button>
        <div className="row" style={{ gap: 20, justifyContent: 'center' }}>
          <button type="button" className="linkbtn" disabled={props.busy} onClick={props.onResend}>
            Send a new code
          </button>
          <button type="button" className="linkbtn" onClick={props.onRestart}>
            Use a different email
          </button>
        </div>
      </form>
    );
  return (
    <div className="pulse">
      <main className="signin">
        <div className="stack" style={{ gap: 16, alignItems: 'center', width: '100%' }}>
          <section className="card signin-card" aria-labelledby="si-title">
            {brand}
            {body}
          </section>
          <p style={{ maxWidth: 400, margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>Only people on the Ten24 team can sign in. Ask an admin for an invitation.</p>
        </div>
      </main>
    </div>
  );
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** What went wrong, in the words the prototype uses. */
export function signInError(error: unknown): string {
  if (error instanceof AuthApiError) {
    if (error.code === 'AUTH_INVALID_CODE' || error.code === 'AUTH_INVALID_CREDENTIALS') return "That code didn't match. Check the email, or send a new code.";
    if (error.code === 'AUTH_FLOW_EXPIRED') return 'That code has expired. Send a new one.';
    if (error.httpStatus === 429) return 'Too many tries. Wait a minute, then send a new code.';
  }
  return "Pulse couldn't sign you in just now. Try again in a moment.";
}

/** The sign-in page: the view above, driven by the API's emailed-code and Google flows. */
export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const client = useAuthClient();
  const { initiate, complete } = useOtpLogin();
  const { providers } = useSocialProviders();
  // Never bounce back to an auth route after signing in (a spent /auth/callback code, say).
  const rawNext = searchParams.get('next') ?? '/';
  const nextPath = /^\/(login|signup|recovery|verification|mfa|auth)\b/.test(rawNext) ? '/' : rawNext;
  const [step, setStep] = React.useState<SignInStep>('start');
  const [email, setEmail] = React.useState('');
  const [flowId, setFlowId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(searchParams.get('error'));
  const [info, setInfo] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const send = async (address: string, resend: boolean): Promise<void> => {
    setError(null);
    setInfo(null);
    if (!EMAIL.test(address)) {
      setError('Enter your work email.');
      return;
    }
    setBusy(true);
    try {
      const started = await initiate.run({ email: address });
      setFlowId(started.flowId);
      setEmail(address);
      setStep('code');
      if (resend) setInfo('We sent a new code. Use the latest one.');
    } catch (reason) {
      setError(signInError(reason));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string): Promise<void> => {
    setError(null);
    setInfo(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6 digits.');
      return;
    }
    if (flowId === null) return;
    setBusy(true);
    try {
      const outcome = await complete.run({ flowId, email, code });
      if (outcome.status === 'authenticated') navigate(nextPath, { replace: true });
      else setError("This account has two-step verification on, which an emailed code can't pass. Continue with Google instead.");
    } catch (reason) {
      setError(signInError(reason));
    } finally {
      setBusy(false);
    }
  };

  const google = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const start = await client.startSocialLogin({ provider: 'google', returnTo: `${window.location.origin}/auth/callback` });
      if (start.initCode !== undefined) window.localStorage.setItem(SOCIAL_LOGIN_PENDING_KEY, JSON.stringify({ initCode: start.initCode, next: nextPath, provider: 'google', mode: 'login' }));
      window.location.assign(start.redirectUrl);
    } catch (reason) {
      setBusy(false);
      setError(signInError(reason));
    }
  };

  return (
    <SignInView
      key={step}
      step={step}
      email={email}
      error={error}
      info={info}
      busy={busy}
      google={providers.some((provider) => provider.id === 'google')}
      onGoogle={() => void google()}
      onSend={(address) => void send(address, false)}
      onVerify={(code) => void verify(code)}
      onResend={() => void send(email, true)}
      onRestart={() => {
        setStep('start');
        setError(null);
        setInfo(null);
      }}
    />
  );
}
